import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FilterDropdown, { type FilterOption } from '../../../components/custom-input/FilterDropdown';
import { Loader } from '../../../components';
import ActionMenu from '../../../components/action-buttons/ActionButtons';
import PagedTable, { type PagedTableHeader } from '../../../components/sorted-table/PagedTable';
import RemoveListingModal from './remove-listing-modal/RemoveListingModal';
import type { MarketPlaceSpecimenResponse } from '../../../types/interfaces/marketPlace.interfaces';
import {
  downloadMarketplaceListingsReport,
  getMarketplaceSpecimensOData,
  removeMarketplaceListing,
} from '../../../api/marketPlaceSpecimenApi';
import { showToast } from '../../../services/toast.service';
import { centsToDollars } from '../../../services/account.service';
import { toastMessages } from '../../../constants/errorMessages';
import { DownloadSVG, SearchSVG } from '../../../components/svgs';
import {
  BUTTON_CONSTANTS,
  LABEL_CONSTANTS,
  MARKETPLACE_LISTING_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../../constants/common';
import { StandardParamsToODataParams, type StandardParams } from '../../../types/interfaces/apiParams.interfaces';
import { dateFormatMMDDYYYY } from '../../../utils/commonUtils';
import './listing.scss';

export interface MarketplaceListingRow {
  id: string;
  productName: string;
  productId: string;
  category: string;
  breed: string;
  seller: string;
  qtyListed: number;
  pricePerUnit: number;
  status: string;
  dateListed: string;
}

interface MarketplaceListingCardProps {
  row: MarketplaceListingRow;
  onRemoveFrom: (row: MarketplaceListingRow) => void;
}

type ListingFiltersChange = {
  productType?: FilterOption<string>;
  breed?: FilterOption<string>;
  seller?: FilterOption<string>;
  searchQuery?: string;
};

type ListingFilterOverrides = {
  productType?: string;
  breed?: string;
  seller?: string;
  search?: string;
};

const LISTING_PAGED_HEADERS: PagedTableHeader[] = [
  { displayName: TABLE_HEADER_CONSTANTS.ANIMAL },
  { displayName: TABLE_HEADER_CONSTANTS.ANIMAL_CODE },
  { displayName: TABLE_HEADER_CONSTANTS.CATEGORY },
  { displayName: TABLE_HEADER_CONSTANTS.BREED },
  { displayName: TABLE_HEADER_CONSTANTS.SELLER_BREEDER },
  { displayName: TABLE_HEADER_CONSTANTS.QTY_LISTED, sortValue: 'quantity' },
  { displayName: TABLE_HEADER_CONSTANTS.PRICE_UNIT, sortValue: 'unitPriceInCents' },
  { displayName: TABLE_HEADER_CONSTANTS.DATE_LISTED, sortValue: 'createdDatetime' },
  { displayName: TABLE_HEADER_CONSTANTS.ACTION },
];

const LISTING_ODATA_DEFAULT_SORT = 'createdDatetime^';

const DEFAULT_PRODUCT_TYPE_OPTIONS: FilterOption<string>[] = [{ name: LABEL_CONSTANTS.ALL_TYPES, value: '' }];
const DEFAULT_BREED_OPTIONS: FilterOption<string>[] = [{ name: LABEL_CONSTANTS.ALL_BREEDS, value: '' }];
const DEFAULT_SELLER_OPTIONS: FilterOption<string>[] = [{ name: LABEL_CONSTANTS.ALL_SELLERS, value: '' }];

const extractUniqueFilterOptions = (
  data: MarketPlaceSpecimenResponse[],
): {
  productType: FilterOption<string>[];
  breed: FilterOption<string>[];
  seller: FilterOption<string>[];
} => {
  const productTypeSet = new Set<string>();
  const breedSet = new Set<string>();
  const sellerById = new Map<number, string>();
  data.forEach(item => {
    const productTypeName = item.specimen?.specimenType?.name?.trim();
    if (productTypeName) productTypeSet.add(productTypeName);
    const breedName = item.breed?.name?.trim() ?? item.animal?.breed?.name?.trim();
    if (breedName) breedSet.add(breedName);
    const accountId = item.accountId ?? item.account?.accountId;
    const sellerName = item.account?.name?.trim();
    if (accountId != null && sellerName) sellerById.set(Number(accountId), sellerName);
  });
  const sellerOptionsList = Array.from(sellerById.entries())
    .sort((first, second) => first[1].localeCompare(second[1]))
    .map(([id, name]) => ({ name, value: String(id) }));
  return {
    productType: [
      { name: LABEL_CONSTANTS.ALL_TYPES, value: '' },
      ...Array.from(productTypeSet)
        .sort((first, second) => first.localeCompare(second))
        .map(name => ({ name, value: name })),
    ],
    breed: [
      { name: LABEL_CONSTANTS.ALL_BREEDS, value: '' },
      ...Array.from(breedSet)
        .sort((first, second) => first.localeCompare(second))
        .map(name => ({ name, value: name })),
    ],
    seller: [{ name: LABEL_CONSTANTS.ALL_SELLERS, value: '' }, ...sellerOptionsList],
  };
};

const mergeFilterOptions = (
  current: FilterOption<string>[],
  fromResponse: FilterOption<string>[],
  defaultFirst: FilterOption<string>,
): FilterOption<string>[] => {
  const byValue = new Map<string, FilterOption<string>>();
  byValue.set('', defaultFirst);
  current.forEach(option => byValue.set(String(option.value), option));
  fromResponse.forEach(option => byValue.set(String(option.value), option));
  const rest = Array.from(byValue.entries())
    .filter(([val]) => val !== '')
    .map(([, option]) => option)
    .sort((first, second) => first.name.localeCompare(second.name));
  return [defaultFirst, ...rest];
};
const mapSpecimenResponseToRow = (item: MarketPlaceSpecimenResponse): MarketplaceListingRow => {
  const pricePerUnit = typeof item.unitPriceInCents === 'number' ? item.unitPriceInCents : 0;
  const qtyListed = typeof item.quantity === 'number' ? item.quantity : 0;
  const dateListed = item.createdDatetime ? dateFormatMMDDYYYY(new Date(item.createdDatetime)) : '';
  return {
    id: String(item.marketPlaceSpecimenId),
    productName: item.animal?.name ?? '',
    productId: item.animal?.code ?? '',
    category: item.specimen?.specimenType?.name ?? '',
    breed: item.breed?.name ?? item.animal?.breed?.name ?? '',
    seller: item.account?.name ?? '',
    qtyListed,
    pricePerUnit,
    status: item.isDeleted ? LABEL_CONSTANTS.INACTIVE : LABEL_CONSTANTS.ACTIVE,
    dateListed,
  };
};

const MarketplaceListingCard = ({ row, onRemoveFrom }: MarketplaceListingCardProps): JSX.Element => (
  <div className="card max-width marketplace-listing__card">
    <div className="marketplace-listing-card">
      <div className="marketplace-listing-card__content">
        <div className="marketplace-listing-card__grid">
          <div className="card-content-section">
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.ANIMAL}</label>
              <label>{row.productName}</label>
            </div>
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.ANIMAL_ID}</label>
              <label>{row.productId}</label>
            </div>
            <div className="right column">
              <label>{TABLE_HEADER_CONSTANTS.CATEGORY}</label>
              <label>{row.category}</label>
            </div>
          </div>
          <div className="card-content-section">
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.BREED}</label>
              <label>{row.breed}</label>
            </div>
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.SELLER_BREEDER}</label>
              <label>{row.seller}</label>
            </div>
            <div className="right column">
              <label>{TABLE_HEADER_CONSTANTS.QTY_LISTED}</label>
              <label>{row.qtyListed}</label>
            </div>
          </div>
          <div className="card-content-section">
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.PRICE_UNIT}</label>
              <label>{`$${centsToDollars(row.pricePerUnit)}`}</label>
            </div>
            <div className="right column">
              <label>{TABLE_HEADER_CONSTANTS.DATE_LISTED}</label>
              <label>{row.dateListed}</label>
            </div>
          </div>
        </div>
      </div>
      <div className="marketplace-listing-card__action">
        <ActionMenu actionButtons={[{ name: BUTTON_CONSTANTS.REMOVE, action: () => onRemoveFrom(row) }]} />
      </div>
    </div>
  </div>
);

const listingFetchLimit = 10;

const Listing = (): JSX.Element => {
  const [productTypeOptions, setProductTypeOptions] = useState<FilterOption<string>[]>(() => DEFAULT_PRODUCT_TYPE_OPTIONS);
  const [productType, setProductType] = useState<FilterOption<string>>(() => DEFAULT_PRODUCT_TYPE_OPTIONS[0]);
  const [breedOptions, setBreedOptions] = useState<FilterOption<string>[]>(() => DEFAULT_BREED_OPTIONS);
  const [breed, setBreed] = useState<FilterOption<string>>(() => DEFAULT_BREED_OPTIONS[0]);
  const [sellerOptions, setSellerOptions] = useState<FilterOption<string>[]>(() => DEFAULT_SELLER_OPTIONS);
  const [seller, setSeller] = useState<FilterOption<string>>(() => DEFAULT_SELLER_OPTIONS[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [listings, setListings] = useState<MarketplaceListingRow[]>([]);
  const [isRemoveListingModalOpen, setIsRemoveListingModalOpen] = useState(false);
  const [listingToRemove, setListingToRemove] = useState<MarketplaceListingRow | null>(null);
  const [isMobileView, setIsMobileView] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const [tableRefresh, setTableRefresh] = useState(0);

  const ref = useRef<any>(null);
  const isLoadingAdditionalPage: MutableRefObject<boolean> = useRef<boolean>(false);
  const hasAllResults: MutableRefObject<boolean> = useRef<boolean>(false);
  const callNumber: MutableRefObject<number> = useRef<number>(0);
  const skipNextOffsetEffect = useRef<boolean>(false);

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobileView(window.innerWidth <= 767);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    isLoadingAdditionalPage.current = false;
  }, [listings]);

  const buildListingODataParams = useCallback(
    (overrides?: ListingFilterOverrides, currentOffset = 0): StandardParams => {
      const productTypeVal = overrides?.productType ?? productType.value;
      const breedVal = overrides?.breed ?? breed.value;
      const sellerVal = overrides?.seller ?? seller.value;
      const searchVal = (overrides?.search ?? searchQuery).trim().replaceAll("'", "''");
      const standard: StandardParams = {
        include: 'Account, Animal.Breed.Species, Animal.Attachments, Specimen.SpecimenType',
        filter: 'IsDeleted eq false',
        limit: listingFetchLimit,
        offset: currentOffset,
      };
      const clauses: string[] = ['IsDeleted eq false'];
      if (searchVal) {
        clauses.push(`(contains(Animal/Code,'${searchVal}') or contains(Animal/Name,'${searchVal}'))`);
      }
      if (breedVal) clauses.push(`(Animal/Breed/Name eq '${breedVal}')`);
      if (productTypeVal) clauses.push(`(Specimen/SpecimenType/Name eq '${productTypeVal}')`);
      if (sellerVal) clauses.push(`(AccountId eq ${sellerVal})`);
      standard.filter = clauses.join(' and ');
      return standard;
    },
    [productType.value, breed.value, seller.value, searchQuery],
  );

  const listingODataFilter = useMemo(() => buildListingODataParams(undefined, 0).filter ?? '', [buildListingODataParams]);

  const getListingTableData = useCallback(async (params: StandardParams) => {
    const response = await getMarketplaceSpecimensOData(StandardParamsToODataParams(params));
    const raw = response.data;
    const specimens: MarketPlaceSpecimenResponse[] = Array.isArray(raw) ? raw : raw?.result ?? [];
    const rows = specimens.map(mapSpecimenResponseToRow);
    if (params.offset === 0) {
      setListings(rows);
      const extracted = extractUniqueFilterOptions(specimens);
      setProductTypeOptions(prev => mergeFilterOptions(prev, extracted.productType, DEFAULT_PRODUCT_TYPE_OPTIONS[0]));
      setBreedOptions(prev => mergeFilterOptions(prev, extracted.breed, DEFAULT_BREED_OPTIONS[0]));
      setSellerOptions(prev => mergeFilterOptions(prev, extracted.seller, DEFAULT_SELLER_OPTIONS[0]));
    } else {
      setListings(prev => [...prev, ...rows]);
    }
    return response;
  }, []);

  const fetchListings = useCallback(
    async (currentOffset: number, overrides?: ListingFilterOverrides) => {
      if (hasAllResults.current && currentOffset >= 0) return;

      callNumber.current++;
      const callNum = callNumber.current;

      try {
        setLoading(true);

        const standard = buildListingODataParams(overrides, currentOffset);
        const response = await getMarketplaceSpecimensOData(StandardParamsToODataParams(standard));
        const data = response.data;
        const specimens: MarketPlaceSpecimenResponse[] = Array.isArray(data) ? data : data?.result ?? [];

        if (currentOffset === 0 && ref.current) {
          ref.current.scroll({ top: 0 });
        }

        if (callNum === callNumber.current) {
          const rows = specimens.map(mapSpecimenResponseToRow);

          setListings(prev => (currentOffset > 0 ? [...prev, ...rows] : rows));
          hasAllResults.current = specimens.length < listingFetchLimit;

          const extracted = extractUniqueFilterOptions(specimens);
          setProductTypeOptions(prev => mergeFilterOptions(prev, extracted.productType, DEFAULT_PRODUCT_TYPE_OPTIONS[0]));
          setBreedOptions(prev => mergeFilterOptions(prev, extracted.breed, DEFAULT_BREED_OPTIONS[0]));
          setSellerOptions(prev => mergeFilterOptions(prev, extracted.seller, DEFAULT_SELLER_OPTIONS[0]));
        }
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        if (currentOffset === 0) setListings([]);
      } finally {
        setLoading(false);
      }
    },
    [buildListingODataParams],
  );

  const applyFilters = useCallback(
    (overrides?: ListingFilterOverrides) => {
      if (!isMobileView) return;
      hasAllResults.current = false;
      if (offset === 0) {
        fetchListings(0, overrides);
      } else {
        skipNextOffsetEffect.current = true;
        setOffset(0);
        fetchListings(0, overrides);
      }
    },
    [fetchListings, offset, isMobileView],
  );

  useEffect(() => {
    if (!isMobileView) return;
    if (skipNextOffsetEffect.current) {
      skipNextOffsetEffect.current = false;
      return;
    }
    fetchListings(offset);
  }, [offset, fetchListings, isMobileView]);

  const loadMore = () => {
    setOffset(prev => prev + listingFetchLimit);
  };

  const handleScroll = useCallback(() => {
    if (hasAllResults.current || isLoadingAdditionalPage.current) return;

    let scrollTop: number, scrollHeight: number, clientHeight: number;

    if (window.innerWidth <= 767) {
      scrollTop = window.scrollY;
      clientHeight = window.innerHeight;
      scrollHeight = document.documentElement.scrollHeight;
    } else {
      const el = ref.current;
      if (!el) return;
      scrollTop = el.scrollTop;
      clientHeight = el.clientHeight;
      scrollHeight = el.scrollHeight;
    }

    if (scrollTop + clientHeight >= scrollHeight - 150) {
      isLoadingAdditionalPage.current = true;
      loadMore();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('touchmove', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
    };
  }, [handleScroll]);

  const handleFiltersChange = useCallback(
    (changes: ListingFiltersChange) => {
      if (changes.productType) {
        setProductType(changes.productType);
      }
      if (changes.breed) {
        setBreed(changes.breed);
      }
      if (changes.seller) {
        setSeller(changes.seller);
      }
      if (changes.searchQuery !== undefined) {
        setSearchQuery(changes.searchQuery);
      }

      if (isMobileView) {
        applyFilters({
          productType: changes.productType?.value ?? productType.value,
          breed: changes.breed?.value ?? breed.value,
          seller: changes.seller?.value ?? seller.value,
          search: changes.searchQuery ?? searchQuery,
        });
      }
    },
    [applyFilters, isMobileView, productType.value, breed.value, seller.value, searchQuery],
  );

  const handleApply = useCallback(() => {
    if (!isMobileView) return;

    applyFilters({
      productType: productType.value,
      breed: breed.value,
      seller: seller.value,
      search: searchQuery,
    });
  }, [isMobileView, applyFilters, productType, breed, seller, searchQuery]);

  const handleClearAll = useCallback(() => {
    setProductType(productTypeOptions[0]);
    setBreed(breedOptions[0]);
    setSeller(sellerOptions[0]);
    setSearchQuery('');

    if (isMobileView) {
      applyFilters({
        productType: '',
        breed: '',
        seller: '',
        search: '',
      });
    }
  }, [applyFilters, isMobileView, productTypeOptions, breedOptions, sellerOptions]);

  const getExportODataParams = useCallback(() => {
    const standard = buildListingODataParams();
    return StandardParamsToODataParams({
      ...standard,
      sort: LISTING_ODATA_DEFAULT_SORT,
    });
  }, [buildListingODataParams]);

  const setExportReportName = useCallback((link: HTMLAnchorElement): void => {
    const prefix = 'Marketplace_Listings';
    const currentDate = dateFormatMMDDYYYY(new Date());
    const fileName = `${prefix}_${currentDate}.xlsx`;
    link.setAttribute('download', fileName);
  }, []);

  const handleExportData = useCallback(async () => {
    if (isExporting) return;

    try {
      setIsExporting(true);

      const response = await downloadMarketplaceListingsReport({}, getExportODataParams());
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      setExportReportName(link);
      document.body.appendChild(link);
      link.click();
    } catch {
      showToast.error(toastMessages.FAILED_TO_DOWNLOAD_REPORT);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, getExportODataParams, setExportReportName]);

  const handleRemoveFrom = useCallback((row: MarketplaceListingRow) => {
    setListingToRemove(row);
    setIsRemoveListingModalOpen(true);
  }, []);

  const handleCloseRemoveListingModal = useCallback(() => {
    setIsRemoveListingModalOpen(false);
    setListingToRemove(null);
  }, []);

  const handleConfirmRemoveListing = useCallback(
    (row: MarketplaceListingRow, reason: string) => {
      const id = Number(row.id);
      if (Number.isNaN(id)) return;
      removeMarketplaceListing(id, reason)
        .then(() => {
          handleCloseRemoveListingModal();
          if (isMobileView) {
            fetchListings(0);
          } else {
            setTableRefresh(previous => previous + 1);
          }
        })
        .catch(() => {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        });
    },
    [handleCloseRemoveListingModal, fetchListings, isMobileView],
  );

  return (
    <div className="marketplace-listing">
      <div className="marketplace-listing__panel">
        <div className="marketplace-listing__header">
          <div className="marketplace-listing__header-text">
            <h1 className="marketplace-listing__title">{PAGE_HEADER_CONSTANTS.MARKETPLACE_LISTING}</h1>
            <p className="marketplace-listing__subtitle">{MARKETPLACE_LISTING_CONSTANTS.ACTIVE_LISTINGS}</p>
          </div>
          <button
            type="button"
            className={`button outlined download-report marketplace-listing__download ${isExporting ? 'disabled' : ''}`}
            onClick={handleExportData}
            disabled={isExporting}>
            <DownloadSVG />
            &nbsp;{LABEL_CONSTANTS.DOWNLOAD}
          </button>
        </div>

        <div className="marketplace-listing__filters">
          <div className="marketplace-listing__filter-group">
            <label className="marketplace-listing__filter-label">{MARKETPLACE_LISTING_CONSTANTS.PRODUCT_TYPE}</label>
            <FilterDropdown
              options={productTypeOptions}
              value={productType}
              onChange={option => handleFiltersChange({ productType: option })}
              placeholder="Choose One"
            />
          </div>
          <div className="marketplace-listing__filter-group">
            <label className="marketplace-listing__filter-label">{MARKETPLACE_LISTING_CONSTANTS.BREED}</label>
            <FilterDropdown
              options={breedOptions}
              value={breed}
              onChange={option => handleFiltersChange({ breed: option })}
              placeholder="Choose One"
            />
          </div>
          <div className="marketplace-listing__filter-group">
            <label className="marketplace-listing__filter-label">{MARKETPLACE_LISTING_CONSTANTS.SELLER}</label>
            <FilterDropdown
              options={sellerOptions}
              value={seller}
              onChange={option => handleFiltersChange({ seller: option })}
              placeholder="Choose One"
            />
          </div>
          <div className="marketplace-listing__filter-group marketplace-listing__filter-group--search">
            <label className="marketplace-listing__filter-label">{LABEL_CONSTANTS.SEARCH}</label>
            <div className="marketplace-listing__search">
              <input
                type="search"
                className="marketplace-listing__search-input"
                placeholder={MARKETPLACE_LISTING_CONSTANTS.SEARCH_PLACEHOLDER}
                value={searchQuery}
                onChange={event => handleFiltersChange({ searchQuery: event.target.value })}
              />
              <span className="marketplace-listing__search-icon" aria-hidden>
                <SearchSVG />
              </span>
            </div>
          </div>
          <div className="marketplace-listing__filter-actions">
            <button type="button" className="button green listing__apply-button" onClick={handleApply}>
              {BUTTON_CONSTANTS.APPLY}
            </button>
            <button type="button" className="button secondary listing__clear-button" onClick={handleClearAll}>
              {BUTTON_CONSTANTS.CLEAR_ALL}
            </button>
          </div>
        </div>

        <div className="marketplace-listing__content">
          <div className="desk-marketplace-listing">
            {!isMobileView && (
              <PagedTable
                headers={LISTING_PAGED_HEADERS}
                getData={getListingTableData}
                buildRow={(item: MarketPlaceSpecimenResponse) => {
                  const row = mapSpecimenResponseToRow(item);
                  return [
                    row.productName,
                    row.productId,
                    row.category,
                    row.breed,
                    row.seller,
                    row.qtyListed,
                    `$${centsToDollars(row.pricePerUnit)}`,
                    row.dateListed,
                    <ActionMenu
                      key={`listing-actions-${row.id}`}
                      actionButtons={[{ name: BUTTON_CONSTANTS.REMOVE, action: () => handleRemoveFrom(row) }]}
                    />,
                  ];
                }}
                include="Account, Animal.Breed.Species, Animal.Attachments, Specimen.SpecimenType"
                filter={listingODataFilter}
                height={500}
                minHeight={156}
                pageSize={10}
                scrollBufferThickness={100}
                sortBy="createdDatetime"
                sortDescByDefault
                refresh={tableRefresh}
                setRefresh={setTableRefresh}
                noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
              />
            )}
          </div>

          <div className="xs-marketplace-listing" ref={ref}>
            {!loading && listings.length === 0 ? (
              <div className="marketplace-listing__no-results">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
            ) : (
              listings.length > 0 &&
              listings.map(row => <MarketplaceListingCard key={row.id} row={row} onRemoveFrom={handleRemoveFrom} />)
            )}
            {loading && <Loader addedSpace loaderSize="small" />}
          </div>
        </div>
      </div>

      <RemoveListingModal
        isOpen={isRemoveListingModalOpen}
        setIsOpen={setIsRemoveListingModalOpen}
        listing={listingToRemove}
        onClose={handleCloseRemoveListingModal}
        onConfirm={handleConfirmRemoveListing}
      />
    </div>
  );
};

export default Listing;
